// React
import { Fragment } from 'react'

// Interfaces
import { IFormGeneratorProps } from './interfaces'
import { IBaseForm, IFormGeneratorCommonProps } from 'interfaces/form'
import { IBackOfficeBasePhotoUploader } from 'components/back-office/BackOfficeBasePhotoUploader/interfaces'
import { IFormTextFieldProps } from '../FormTextField/interfaces'
import { IFormTextAreaProps } from '../FormTextArea/interfaces'
import { IFormCheckboxProps } from '../FormCheckbox/interfaces'

// Antd
import { Row, Col, Form, DatePickerProps } from 'antd'

// Components
import {
  FormSelectOption,
  FormUploadSingleFile,
  BackOfficeBasePhotoUploader,
  FormTextField,
  FormTextArea,
  FormItemContainer,
  FormDatePicker,
  FormCheckbox,
} from 'components'

// i18n
import { useTranslation } from 'react-i18next'

// Hook
import { useValidation } from 'hooks'

// Constant
import { FORM_INPUT } from 'constant/form.constant'

// Lodash
import omit from 'lodash/omit'
import { IFormSelectOptionProps } from '../FormSelectOption/interfaces'

// Mobx
import { observer } from 'mobx-react'

const FormGenerator = observer(({ fields, form, onChange, reduceMargin }: IFormGeneratorProps): any => {
  // Hook
  const { t } = useTranslation()
  const { validation_required, validation_onlyNumber, validation_validEmail, validation_alphaNumeric } = useValidation()

  /**
   * @description Render label
   *
   * @param {string} label
   * @param {object} options
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderLabel = (label: string, options?: { reduceMargin: boolean }): JSX.Element => {
    return (
      <p className={`font-14 text-grey font-weight-400 ${options?.reduceMargin ? 'mb-1' : 'mb-2'}`} key={label}>
        {t(label)}
      </p>
    )
  }

  /**
   * @description Render Select Option input
   *
   * @param {string} formName
   * @param {IFormGeneratorCommonProps} formSelectOptionProps
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderSelectOption = (formName: string, formSelectOptionProps: IFormSelectOptionProps): JSX.Element => {
    const onChangeSelectOption = (event: any) => {
      if (typeof onChange === 'function') {
        onChange(formName, event)
      }
    }

    return (
      <FormSelectOption {...formSelectOptionProps} list={formSelectOptionProps?.list || []} onChange={onChangeSelectOption} />
    )
  }

  /**
   * @description Render single upload file
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderSingleUploadFile = (fileName: any, commonProps: IFormGeneratorCommonProps | { error: string }): JSX.Element => {
    /** @note Just to trigger the reset */
    const onChangeFile = (): void => {
      return
    }

    return <FormUploadSingleFile fileName={fileName} onChange={onChangeFile} {...commonProps} />
  }

  /**
   * @description Render base upload file
   *
   * @param {string} formName
   * @param {IBackOfficeBasePhotoUploader} basePhotoUploaderProps
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderBasePhotoUploader = (
    formName: any,
    basePhotoUploaderProps: IBackOfficeBasePhotoUploader | { error: string },
  ): JSX.Element => {
    const onChangeFile = async (file: File | null): Promise<any> => {
      // Emit the value
      onChange?.(formName, file)
    }

    return <BackOfficeBasePhotoUploader {...basePhotoUploaderProps} onChange={onChangeFile} />
  }

  /**
   * @description Render Text Field
   *
   * @param {IFormTextFieldProps} formTextFieldProps
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderTextField = (formTextFieldProps: IFormTextFieldProps): JSX.Element => {
    return <FormTextField {...formTextFieldProps} />
  }

  /**
   * @description Render Text Area
   *
   * @param {IFormTextAreaProps} formTextAreaProps
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderTextArea = (formTextAreaProps: IFormTextAreaProps): JSX.Element => {
    return <FormTextArea {...formTextAreaProps} />
  }

  /**
   * @description Render Date Picker
   *
   * @param {DatePickerProps} datePickerProps
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderDatePicker = (datePickerProps: DatePickerProps): JSX.Element => {
    return <FormDatePicker {...datePickerProps} />
  }

  /**
   * @description Render checkbox
   *
   * @param {CheckboxProps} checkboxProps
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderCheckbox = (checkboxProps: IFormCheckboxProps): JSX.Element => {
    return <FormCheckbox {...checkboxProps} checkboxLabel={checkboxProps.checkboxLabel} />
  }

  /**
   * @description Render rules
   *
   * @param {array} validations
   * @param {string} fieldName
   *
   * @return {array} array
   */
  const renderRules = (validations: string[], fieldName: string) => {
    const rules = []

    // Required validation
    if (validations.includes('required')) rules.push(validation_required(fieldName))
    if (validations.includes('email')) rules.push(validation_validEmail())

    // Push custom validator
    rules.push({
      validator: (_: any, value: string) => {
        if (validations.includes('numeric')) {
          if (!value || validation_onlyNumber(value)) return Promise.resolve()
          if (!validation_onlyNumber(value)) return Promise.reject(new Error(`${fieldName} ${t('app.validations.number')}`))
        }

        if (validations.includes('alphaNumeric')) {
          if (!value || validation_alphaNumeric(value)) return Promise.resolve()
          if (!validation_alphaNumeric(value))
            return Promise.reject(new Error(`${fieldName} ${t('app.validations.alphaNumeric')}`))
        }

        return Promise.resolve()
      },
    })

    return rules
  }

  /**
   * @description Render inputs
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderInputs = ({ label, input, isColumn }: IBaseForm): JSX.Element => {
    const commonProps = {
      loading: input?.loading || false,
      disabled: input?.disabled || false,
    }
    const commonFormItemProps = {
      id: input?.formName || '',
      name: input?.formName || '',
      rules: renderRules(input?.rules || [], t(label?.label || '')),
    }
    // Label
    const layout = label?.layout
    const labelBreakpoint = label?.breakpoint || 0

    // Input
    const inputBreakpoint = input?.breakpoint
    const inputName = input?.inputName
    const formName = input?.formName
    const withoutFormItem = input?.withoutFormItem

    return (
      <Container isColumn={isColumn}>
        {/* Render Horizontal Label */}
        {layout === 'horizontal' && (
          <Col xs={24} sm={24} md={labelBreakpoint} lg={labelBreakpoint} xl={labelBreakpoint}>
            {renderLabel(label?.label || '', { reduceMargin: true })}
          </Col>
        )}

        <Col xs={24} sm={24} md={inputBreakpoint} lg={inputBreakpoint} xl={inputBreakpoint}>
          {/* Render Vertical Label */}
          {layout === 'vertical' && renderLabel(label?.label || '', { reduceMargin: true })}

          {/* Render Select Option */}
          {inputName === FORM_INPUT.SELECT_OPTION && (
            <Form.Item id={formName} name={formName} rules={renderRules(input?.rules || [], t(label?.label || ''))}>
              {renderSelectOption(formName || '', { ...commonProps, ...input?.selectOptionAttrs })}
            </Form.Item>
          )}

          {/* Render Text Field */}
          {inputName === FORM_INPUT.TEXT_FIELD && (
            <FormItemContainer noStyle={false} shouldUpdate={false} commonFormItemProps={commonFormItemProps}>
              {renderTextField({ ...omit(commonProps, ['loading']), ...input?.inputAttrs })}
            </FormItemContainer>
          )}

          {/* Render Text Area */}
          {inputName === FORM_INPUT.TEXT_AREA && (
            <FormItemContainer noStyle={false} shouldUpdate={false} commonFormItemProps={commonFormItemProps}>
              {renderTextArea({ ...omit(commonProps, ['loading']), ...input?.inputAttrs })}
            </FormItemContainer>
          )}

          {/* Render Date Picker */}
          {inputName === FORM_INPUT.DATE_PICKER && (
            <FormItemContainer noStyle={false} shouldUpdate={false} commonFormItemProps={commonFormItemProps}>
              {renderDatePicker({ ...omit(commonProps, ['loading']), ...input?.datePickerAttrs })}
            </FormItemContainer>
          )}

          {/* Render Checkbox */}
          {inputName === FORM_INPUT.CHECKBOX && !withoutFormItem ? (
            <FormItemContainer
              noStyle={false}
              shouldUpdate={false}
              valuePropName="checked"
              commonFormItemProps={commonFormItemProps}
            >
              {renderCheckbox({ ...input?.checkboxAttrs })}
            </FormItemContainer>
          ) : (
            inputName === FORM_INPUT.CHECKBOX && renderCheckbox({ ...input?.checkboxAttrs })
          )}

          {/* Render Single Upload File */}
          {inputName === FORM_INPUT.SINGLE_UPLOAD_FILE && (
            <Form.Item noStyle shouldUpdate>
              {() => {
                return (
                  <Form.Item id={formName} name={formName} rules={renderRules(input?.rules || [], t(label?.label || ''))} noStyle>
                    {renderSingleUploadFile(form.getFieldValue(formName || '')?.name, {
                      ...commonProps,
                      error: form.getFieldError(formName || '')?.toString(),
                    })}
                  </Form.Item>
                )
              }}
            </Form.Item>
          )}

          {/* Render Base Photo Uploader */}
          {inputName === FORM_INPUT.BASE_PHOTO_UPLOADER && (
            <Form.Item noStyle shouldUpdate>
              {() => {
                return (
                  <Form.Item id={formName} name={formName} rules={renderRules(input?.rules || [], t(label?.label || ''))} noStyle>
                    {renderBasePhotoUploader(formName, {
                      ...input?.basePhotoUploaderAttrs,
                      image: form.getFieldValue(formName || ''),
                      error: form.getFieldError(formName || '')?.toString(),
                    })}
                  </Form.Item>
                )
              }}
            </Form.Item>
          )}
        </Col>
      </Container>
    )
  }

  /**
   * @description Render container
   *
   * @return {JSX.Element} JSX.Element
   */
  const Container = ({ children, isColumn }: any): JSX.Element => {
    return !isColumn ? <Row className={`${reduceMargin ? 'mb-2' : 'mb-4'} `}>{children}</Row> : <Fragment>{children}</Fragment>
  }

  return fields.length !== 0 ? (
    fields.map((field, fieldIndex) => {
      return field?.columns ? (
        <Row gutter={field.rowGutter} className="mb-4" key={fieldIndex}>
          {field?.columns?.map(({ label, input }, columnIndex) => (
            <Fragment key={columnIndex}>{renderInputs({ label, input, isColumn: true })}</Fragment>
          ))}
        </Row>
      ) : (
        <Fragment key={fieldIndex}>{renderInputs({ label: field.label, input: field.input, isColumn: field.isColumn })}</Fragment>
      )
    })
  ) : (
    <></>
  )
})

export { FormGenerator }
